import _ from 'lodash';
import { initializeCircles, isEqualDeep } from '../../utils';
import {
	DRIVER,
	DRIVER_ICON_URL,
	SHOP_ICON_URL,
	CUSTOMER_ICON_URL,
} from '../../constants';
import store from '../../redux/store';
import { logMediumError } from '../../utils/logger';

type MapClientRefs = {
	map: any;
	mapCanvas: HTMLDivElement | null;
	directionsService: any;
	directionsDisplay: any;
	orderMarker: any;
	customerMarker: any;
	circle1: any;
	circle2: any;
	orderLocation: any;
};

const getGoogle = () => (window as unknown as { google: any }).google;

const createMarker = (position: any, icon: any, map: any) => {
	const google = getGoogle();
	return new google.maps.Marker({
		position,
		map,
		icon,
	});
};

export const initializeMap = (refs: MapClientRefs, options: any = {}) => {
	const google = getGoogle();
	if (!refs.mapCanvas) return;
	const defaultOptions = {
		mapTypeId: google.maps.MapTypeId.ROADMAP,
		disableDefaultUI: true,
		zoom: 12,
	};
	const myOptions = { ...defaultOptions, ...options };
	refs.map = new google.maps.Map(refs.mapCanvas, myOptions);
	refs.directionsService = new google.maps.DirectionsService();
};

export const initializeMapWithAddress = (
	address: any,
	refs: MapClientRefs
) => {
	try {
		const { latitude, longitude } = address;
		const { secondaryColor } = store.getState().configurationReducer;
		const center = { lat: latitude, lng: longitude };
		const options = {
			center,
			zoom: 12,
		};
		initializeMap(refs, options);
		initializeCircles(refs as any, center, secondaryColor);
	} catch (error) {
		logMediumError(`Error initializing map with address: ${error}`, {
			address,
		});
	}
};

export const updateMap = (customerLocation: any, refs: MapClientRefs) => {
	try {
		const google = getGoogle();
		const { map } = refs;
		if (!map) return;
		const bounds = new google.maps.LatLngBounds();
		const point = {
			lat: parseFloat(customerLocation.latitude),
			lng: parseFloat(customerLocation.longitude),
		};
		new google.maps.Marker({ position: point, map });
		bounds.extend(point);
		map.setCenter({ lat: point.lat, lng: point.lng });
		map.setZoom(12);
		const circleCenter = { lat: point.lat, lng: point.lng };
		if (refs.circle1) refs.circle1.setCenter(circleCenter);
		if (refs.circle2) refs.circle2.setCenter(circleCenter);
	} catch (error) {
		logMediumError(`Error updating map: ${error}`);
	}
};

export const updateMapDirection = (
	orderLocation: any,
	customerLocation: any,
	mode: string,
	refs: MapClientRefs
) => {
	refs.orderLocation = orderLocation;
	const google = getGoogle();
	const { directionsService, map } = refs;
	if (!map || !directionsService) return;

	const driverIcon = {
		url: DRIVER_ICON_URL,
		scaledSize: new google.maps.Size(70, 70),
	};
	const customerIcon = {
		url: CUSTOMER_ICON_URL,
		scaledSize: new google.maps.Size(40, 40),
	};
	const shopIcon = {
		url: SHOP_ICON_URL,
		scaledSize: new google.maps.Size(40, 40),
	};
	const rendererOptions = {
		map,
		suppressMarkers: true,
		polylineOptions: {
			strokeColor: '#6E747C',
			strokeWidth: 3,
		},
	};
	const start = new google.maps.LatLng(
		orderLocation.latitude,
		orderLocation.longitude
	);
	const end = new google.maps.LatLng(
		customerLocation.latitude,
		customerLocation.longitude
	);
	const request = {
		origin: start,
		destination: end,
		travelMode: google.maps.DirectionsTravelMode.DRIVING,
	};

	directionsService.route(request, (response: any, status: any) => {
		if (status === google.maps.DirectionsStatus.OK) {
			const orderLocationState = refs.orderLocation;

			if (isEqualDeep(orderLocationState, orderLocation)) {
				const route = response.routes[0].legs[0];

				if (refs.orderMarker) {
					refs.orderMarker.setMap(null);
				}
				if (refs.customerMarker) {
					refs.customerMarker.setMap(null);
				}

				if (mode === DRIVER) {
					refs.orderMarker = createMarker(
						route.start_location,
						driverIcon,
						map
					);
				} else {
					refs.orderMarker = createMarker(
						route.start_location,
						shopIcon,
						map
					);
				}
				if (
					!_.isEqual(orderLocation, customerLocation) ||
					mode === DRIVER
				) {
					refs.customerMarker = createMarker(
						route.end_location,
						customerIcon,
						map
					);
				}
				if (refs.directionsDisplay) {
					refs.directionsDisplay.setMap(null);
				}
				refs.directionsDisplay = new google.maps.DirectionsRenderer(
					rendererOptions
				);
				refs.directionsDisplay.setMap(map);
				refs.directionsDisplay.setDirections(response);
			}
		}
	});
};
